import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "react-router-dom";
import { z } from "zod";
import {
    User,
    Building2,
    Phone,
    Mail,
    MapPin,
    ArrowRight,
    Loader2,
    CheckCircle2
} from "lucide-react";
import logo from "../../assets/logo.png";
import toast from "react-hot-toast";

const contactSchema = z.object({
    name: z.string().min(2, "Full name is required"),
    pharmacyName: z.string().min(2, "Pharmacy name is required"),
    contactNumber: z.string().min(10, "Valid contact number is required"),
    email: z.string().email("Invalid email address"),
    address: z.string().min(5, "Address is required"),
});

type ContactData = z.infer<typeof contactSchema>;

export function ContactPage() {
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<ContactData>({
        resolver: zodResolver(contactSchema),
    });

    const onSubmit = async (data: ContactData) => {
        setIsLoading(true);
        // Simulate API call
        try {
            await new Promise((resolve) => setTimeout(resolve, 1500));
            console.log("Contact form submitted:", data);

            toast.success("Message sent successfully!", {
                duration: 5000,
                position: "top-center",
                style: {
                    borderLeft: "4px solid #10b981",
                    fontWeight: "600",
                    fontSize: "16px",
                    padding: "16px 24px",
                },
            });

            setIsSubmitted(true);
            reset();
        } catch (err) {
            toast.error("Failed to send message. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="h-screen grid lg:grid-cols-2 bg-slate-50 relative overflow-hidden">
            {/* Decorative Blobs for depth */}
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100/50 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2 -z-10" />
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-teal-50/50 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4 -z-10" />

            {/* Left Side - Immersive Hero Section */}
            <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden bg-blue-600 text-white border-r rounded-tr-[50px] rounded-br-[50px]">
                <div className="absolute inset-0 z-0 text-white">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-600" />
                    <div className="absolute top-[-10%] left-[-10%] w-[100%] h-[100%] rounded-full blur-[120px] animate-pulse" />
                    <div className="absolute bottom-[-10%] right-[-10%] w-[80%] h-[80%] rounded-full bg-white/10 blur-[100px] animate-pulse delay-700" />
                    <div className="absolute top-[20%] left-[10%] w-[60%] h-[60%] rounded-full bg-blue-400/20 blur-[110px] animate-pulse delay-1000" />
                    <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-soft-light" />
                </div>

                <div className="relative z-10">
                    <div className="flex items-center gap-3.5 bg-white backdrop-blur-2xl py-3 px-6 rounded-2xl border border-white/20 shadow-2xl w-fit group hover:bg-white/20 transition-all duration-300">
                        <div className="flex flex-col text-white">
                            <span className="text-5xl font-black tracking-tight leading-none">
                                <span className="text-blue-800">Medi</span>
                                <span className="text-red-700">Core</span>
                                <span className="text-teal-700">Pay</span>
                            </span>
                        </div>
                    </div>
                </div>

                <div className="relative z-10 max-w-lg space-y-8">
                    <div className="space-y-4">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-white text-xs font-bold tracking-wider uppercase">
                            <span className="flex h-1.5 w-1.5 rounded-full bg-teal-300 animate-ping" />
                            Contact Our Team
                        </div>
                        <h1 className="text-6xl font-black leading-[1.1] tracking-tight text-white">
                            Ready to <br />
                            <span className="text-teal-400">Transform</span><br />
                            Your Pharmacy?
                        </h1>
                        <p className="text-lg text-blue-50 font-light leading-relaxed max-w-md">
                            Send us your details and our team will help you get started with the most advanced healthcare commerce platform.
                        </p>
                    </div>
                </div>

                <div className="relative z-10 flex justify-between items-end">
                    <p className="text-sm text-blue-100 font-medium">© 2026 MediCorePay.</p>
                    <div className="flex gap-8">
                        <a href="#" className="text-blue-100 hover:text-white transition-colors text-sm font-semibold">Security</a>
                        <a href="#" className="text-blue-100 hover:text-white transition-colors text-sm font-semibold">Privacy</a>
                    </div>
                </div>
            </div>

            {/* Right Side - Contact Form */}
            <div className="flex flex-col items-center justify-center p-6 lg:p-8 w-full relative overflow-y-auto">
                <div className="w-full max-w-[500px] space-y-4 animate-slide-up">
                    <div className="flex flex-col items-center mb-6">
                        <img
                            src={logo}
                            alt="MediCorePay Logo"
                            className="w-25 h-25 object-contain max-w-[200px] mb-6 group hover:scale-105 transition-all duration-500"
                        />
                        <div className="space-y-4 text-center">
                            <h2 className="text-5xl font-black text-slate-900 tracking-tight">
                                Get in Touch
                            </h2>
                            <p className="text-slate-500 text-lg font-medium">
                                Fill the form below and we'll reach out shortly
                            </p>
                        </div>
                    </div>

                    {isSubmitted ? (
                        <div className="bg-white p-12 rounded-[32px] shadow-2xl border border-emerald-100 text-center space-y-6 animate-scale-up">
                            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto text-emerald-600">
                                <CheckCircle2 size={48} />
                            </div>
                            <div className="space-y-3">
                                <h3 className="text-3xl font-black text-slate-900">Thank You!</h3>
                                <p className="text-slate-600 text-lg font-medium leading-relaxed">
                                    Your request has been received. Our team will contact you shortly to provide access to MediCorePay.
                                </p>
                            </div>
                            <button
                                onClick={() => setIsSubmitted(false)}
                                className="text-indigo-600 font-black flex items-center gap-2 mx-auto hover:gap-3 transition-all underline underline-offset-4 decoration-2 decoration-indigo-100"
                            >
                                Send another message
                            </button>
                        </div>
                    ) : (
                        <form
                            onSubmit={handleSubmit(onSubmit)}
                            className="space-y-4 bg-white/40 backdrop-blur-xl border border-white/60 rounded-[32px] p-6 shadow-2xl relative overflow-hidden"
                        >
                            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03] pointer-events-none" />

                            <div className="space-y-3 relative z-10">
                                {/* Full Name */}
                                <div className="space-y-2">
                                    <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">
                                        Full Name
                                    </label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-blue-500 transition-colors">
                                            <User className="h-4 w-4" />
                                        </div>
                                        <input
                                            {...register("name")}
                                            className="block w-full pl-11 pr-4 py-3 bg-slate-100/50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-blue-500/20 focus:ring-4 focus:ring-blue-500/5 transition-all duration-300 outline-none text-slate-900 placeholder-slate-400 font-bold"
                                            placeholder="John Doe"
                                        />
                                    </div>
                                    {errors.name && (
                                        <p className="text-xs text-red-500 font-bold ml-1">{errors.name.message}</p>
                                    )}
                                </div>

                                {/* Pharmacy Name */}
                                <div className="space-y-2">
                                    <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">
                                        Pharmacy Name
                                    </label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-blue-500 transition-colors">
                                            <Building2 className="h-4 w-4" />
                                        </div>
                                        <input
                                            {...register("pharmacyName")}
                                            className="block w-full pl-11 pr-4 py-3 bg-slate-100/50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-blue-500/20 focus:ring-4 focus:ring-blue-500/5 transition-all duration-300 outline-none text-slate-900 placeholder-slate-400 font-bold"
                                            placeholder="Emerald Pharma"
                                        />
                                    </div>
                                    {errors.pharmacyName && (
                                        <p className="text-xs text-red-500 font-bold ml-1">{errors.pharmacyName.message}</p>
                                    )}
                                </div>

                                {/* Grid for Phone and Email */}
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">
                                            Contact Number
                                        </label>
                                        <div className="relative group">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-blue-500 transition-colors">
                                                <Phone className="h-4 w-4" />
                                            </div>
                                            <input
                                                {...register("contactNumber")}
                                                className="block w-full pl-11 pr-4 py-3 bg-slate-100/50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-blue-500/20 focus:ring-4 focus:ring-blue-500/5 transition-all duration-300 outline-none text-slate-900 placeholder-slate-400 font-bold"
                                                placeholder="1234567890"
                                            />
                                        </div>
                                        {errors.contactNumber && (
                                            <p className="text-xs text-red-500 font-bold ml-1">{errors.contactNumber.message}</p>
                                        )}
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">
                                            Email Address
                                        </label>
                                        <div className="relative group">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-blue-500 transition-colors">
                                                <Mail className="h-4 w-4" />
                                            </div>
                                            <input
                                                {...register("email")}
                                                className="block w-full pl-11 pr-4 py-3 bg-slate-100/50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-blue-500/20 focus:ring-4 focus:ring-blue-500/5 transition-all duration-300 outline-none text-slate-900 placeholder-slate-400 font-bold"
                                                placeholder="john@example.com"
                                            />
                                        </div>
                                        {errors.email && (
                                            <p className="text-xs text-red-500 font-bold ml-1">{errors.email.message}</p>
                                        )}
                                    </div>
                                </div>

                                {/* Located Address */}
                                <div className="space-y-2">
                                    <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">
                                        Located Address
                                    </label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-start pt-4 pointer-events-none text-slate-400 group-focus-within:text-blue-500 transition-colors">
                                            <MapPin className="h-4 w-4" />
                                        </div>
                                        <textarea
                                            {...register("address")}
                                            rows={2}
                                            className="block w-full pl-11 pr-4 py-3 bg-slate-100/50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-blue-500/20 focus:ring-4 focus:ring-blue-500/5 transition-all duration-300 outline-none text-slate-900 placeholder-slate-400 font-bold resize-none"
                                            placeholder="123 Main St, City, Country"
                                        />
                                    </div>
                                    {errors.address && (
                                        <p className="text-xs text-red-500 font-bold ml-1">{errors.address.message}</p>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full relative group py-4 px-6 bg-slate-950 hover:bg-black text-white font-black text-base rounded-2xl shadow-2xl active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden mt-2"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-teal-400 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                    <span className="relative z-10 flex items-center justify-center gap-3">
                                        {isLoading ? (
                                            <>
                                                <Loader2 className="animate-spin h-6 w-6" />
                                                Processing...
                                            </>
                                        ) : (
                                            <>
                                                Submit Request
                                                <ArrowRight className="h-6 w-6 group-hover:translate-x-1.5 transition-transform duration-300" />
                                            </>
                                        )}
                                    </span>
                                </button>
                            </div>
                        </form>
                    )}

                    <div className="text-center pt-4">
                        <p className="text-slate-500 font-bold text-sm">
                            Already have an account?{" "}
                            <Link
                                to="/login"
                                className="text-indigo-600 hover:text-indigo-700 font-black underline underline-offset-4 decoration-2 decoration-indigo-200 hover:decoration-indigo-500 transition-all ml-1"
                            >
                                Sign In
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
